/** English strings for the app's auth area. Keys are camelCase, grouped by screen. */
export default {
  /** Shared auth chrome: the split layout and its prompt panel. */
  layout: {
    homeAria: "Mythex home",
    typewriterPrefix: "Ask Mythex to build ",
    typewriterWords: [
      "your saas startup.",
      "your landing page.",
      "your blog.",
      "your dashboard.",
      "your internal tool.",
      "your prototype.",
      "web apps.",
    ],
    shellPrompt: "Ask Mythex to build your saas startup",
  },

  /** Words and messages shared by several auth screens. */
  emailLabel: "Email",
  emailPlaceholder: "Email",
  passwordLabel: "Password",
  passwordPlaceholder: "Password",
  enterEmail: "Enter your email.",
  enterPassword: "Enter your password.",
  chooseStrongerPassword: "Please choose a stronger password.",
  backToSignIn: "← Back to sign in",
  signIn: "Sign in",
  signUp: "Sign up",

  /** Sign in / Sign up. */
  signInPage: {
    acceptInvitationTitle: "Accept your invitation",
    continueWithGoogle: "Continue with Google",
    continueWithApple: "Continue with Apple",
    or: "Or",
    oauthFailed: "Google sign-in failed. Please try again.",
    googleFailed: "Google sign-in failed. Try again.",
    appleFailed: "Apple sign-in failed. Try again.",
    emailTaken: "That email is already registered — try signing in.",
    emailNotAllowed: "This email address can't be used to sign up. Please use a different one.",
    vpnNotAllowed: "We can't create an account from this connection. Please turn off any VPN or proxy and try again.",
    invalidCredentials: "Wrong email or password.",
    genericError: "Something went wrong. Try again.",
    nameLabel: "Full name",
    namePlaceholder: "Full name (optional)",
    forgotPassword: "Forgot password?",
    haveAccount: "Already have an account? <link>Sign in</link>",
    noAccount: "Don't have an account? <link>Sign up</link>",
    legal: "By continuing, you agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.",
  },

  /** Password field and the strength meter under it. */
  password: {
    show: "Show password",
    hide: "Hide password",
    strength: ["Weak", "Fair", "Good", "Strong"],
    strengthLine: "{strength} · Password must contain at least 8 characters and a number",
  },

  forgotPassword: {
    title: "Forgot password?",
    subtitle: "Enter your email and we'll send you a link to reset your password.",
    sentTitle: "Check your email",
    sentSubtitle: "If an account exists for {email}, we've sent a reset link. It expires in 1 hour.",
    send: "Send reset link",
    sending: "Sending…",
    error: "Something went wrong. Please try again.",
    notReceived: "Didn't get it? Check spam, or <retry>try again</retry>.",
  },

  resetPassword: {
    invalidTitle: "Invalid reset link",
    invalidSubtitle: "This password reset link is missing or malformed.",
    requestNew: "Request a new reset link",
    askFresh: "Ask for a fresh link from the forgot password page.",
    title: "Set a new password",
    subtitle: "Choose a strong new password for your account.",
    doneTitle: "Password updated",
    doneSubtitle: "Your password has been changed. Redirecting to sign in…",
    doneBody: "You can now sign in with your new password.",
    newPasswordLabel: "New password",
    confirmLabel: "Confirm password",
    confirmPlaceholder: "Repeat password",
    mismatchError: "Passwords do not match.",
    mismatch: "Passwords don't match",
    match: "Passwords match",
    expired: "This reset link is invalid or has expired. Request a new one.",
    error: "Something went wrong. Please try again.",
    saving: "Saving…",
    submit: "Set new password",
  },

  verifyEmail: {
    title: "Check your inbox",
    subtitle: "We sent a 6-digit code to <email>{email}</email>. Enter it to continue — verification is required.",
    yourEmail: "your email",
    verified: "Email verified — welcome to Mythex!",
    wrongCode: "That code is wrong or expired. Try again or resend.",
    resent: "A new code is on its way.",
    resendFailed: "Couldn't resend the code. Try again in a minute.",
    switchAccount: "Use a different account",
    notReceived: "Didn't get it?",
    resendIn: "Resend in {n}s",
    resend: "Resend code",
  },

  invite: {
    notFoundTitle: "Invitation not found",
    wrongAccountTitle: "Wrong account",
    invitedTitle: "You're invited",
    joinTitle: "Join {workspace}",
    workspaceFallback: "workspace",
    invalidSubtitle: "This invite link is invalid, expired, or was already used.",
    mismatchSubtitle: "This invitation was sent to {invited}, but you're signed in as {current}.",
    loadingSubtitle: "Join a workspace and start building together on Mythex.",
    readySubtitle: "{inviter} invited {email} to collaborate.",
    askOwner: "Ask the workspace owner to send a new invite.",
    goHome: "Go home",
    signInInvited: "Sign in with the invited email to accept.",
    switchAccount: "Switch account",
    accept: "Accept invitation",
    createToAccept: "Create account to accept",
    haveAccount: "I already have an account",
    joined: "You joined {workspace}!",
  },

  mcpConsent: {
    title: "Connect Mythex MCP",
    intro: "A client (for example Claude) wants to access your sandboxes as this account.",
    grants:
      "This grants permission to create and edit projects, run shell commands in your sandbox, manage secrets (keys you provide), and publish apps.",
    safety:
      "Mythex system prompts and platform secrets are never exposed. Sandbox tools need Pro, which includes sandbox time.",
    notPro: "You can connect now. Tool calls will show an upgrade panel until this account is Pro.",
    invalidLink: "This authorization link is missing or invalid. Start again from Claude.",
    invalidRequest: "Invalid or expired authorization request.",
    noRedirect: "No redirect returned.",
    failed: "Couldn't complete authorization.",
    allow: "Allow access",
    deny: "Deny",
  },
};
